import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import TemplateImageUploader from "../helpers/template_image_uploader"

// The font select (select2), inline text editor (bootstrap-editable plus a character countdown),
// colour pickers (spectrum) and visibility checkboxes for all four editor screens -
// emails/redemption, emails/shipping, webs/thank_you and webs/thank_you_confirmation - which each
// render every widget inside this one mount. They share only that mount and the `updateSettings`
// PUT helper. Image upload/crop (Dropzone + Cropper.js) lives in
// packs/helpers/template_image_uploader.js - its own Dropzone/Cropper instance state was
// independent of everything else here (R9).
//
// Almost every binding goes through jQuery rather than a data-action: select2, spectrum and
// bootstrap-editable all announce through jQuery's event system or a plugin callback, neither of
// which a native listener sees. The plain `.visibility-checkbox` click is bound the same way only
// so that disconnect() tears everything down uniformly.
export default class extends Controller {
  static get values() { return { url: String }; }

  connect() {
    this.settings = {};
    this.imageUploader = new TemplateImageUploader(this.element, this.settings);

    this.imageUploader.bind();
    this.bindTextEvents();
    this.bindColorPickers();
    this.bindVisibilityCheckboxes();
    this.bindFontSelect();
  }

  disconnect() {
    this.imageUploader.unbind();
    this.imageUploader.destroy();
    this.unbindTextEvents();
    this.unbindColorPickers();
    this.unbindVisibilityCheckboxes();
    this.unbindFontSelect();
  }

  // Scoped: every element this controller touches lives inside its own mount, the modal and every
  // editable field included. Still jQuery (R8 justified skip): most call sites feed it straight
  // into select2/spectrum/bootstrap-editable, and the rest chain further jQuery methods off its
  // result throughout the file - splitting those apart would mean rewriting the whole controller
  // for no isolable benefit.
  scoped(selector) {
    return $(this.element).find(selector);
  }

  // The first of `element`'s siblings (same parent, one level) matching `selector` - the native
  // equivalent of jQuery's `.siblings(selector)` narrowed to a single result, for the two spots
  // below that pick one specific sibling rather than enumerating all of them.
  siblingMatching(element, selector) {
    return Array.from(element.parentElement.children).find((sibling) => sibling !== element && sibling.matches(selector));
  }

  logFailedCall(response) {
    console.warn(response);
  }

  // ---- shared settings persistence ----

  updateSettings(node, type, value, onDone) {
    if (this.settings[node] === undefined) {
      this.settings[node] = { [type]: value };
    } else {
      this.settings[node] = Object.assign({}, this.settings[node], { [type]: value });
    }

    const options = RequestHelper.defaultPutOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({ template: { settings: this.settings } });

    fetch(this.urlValue, options).
      then(response => response.json().then(data => ({ ok: response.ok, data }))).
      then(({ ok, data }) => {
        if (ok) {
          window.ltAlert({ message: data.message, duration: 2500 });
          onDone(false);
        } else {
          const message = data.message === undefined ?
            "An internal error occurred. Please try again later." :
            data.message;
          window.ltAlert({ message: message, type: "toast-error" });
          onDone(true);
        }
      }).
      catch(error => {
        onDone(true);
        this.logFailedCall(error);
      });
  }

  // ---- font ----

  bindFontSelect() {
    this.scoped("#font-select").select2({ width: "100%" });
    this.scoped("#font-select").on("change.templates", (event) => {
      this.updateFont(event.currentTarget.value);
    });
  }

  unbindFontSelect() {
    const $select = this.scoped("#font-select");
    $select.off(".templates");
    if ($select.data("select2")) { $select.select2("destroy"); }
  }

  updateFont(font) {
    this.updateSettings("font", "family", font, (error) => {
      if (!error) {
        this.scoped(".custom-font-editable").each((index, element) => {
          element.style.fontFamily = font;
        });
      }
    });
  }

  // ---- inline text (bootstrap-editable) ----

  bindTextEvents() {
    this.scoped(".edit-icon").on("click.templates", (event) => {
      event.preventDefault();
      setTimeout(() => {
        $(event.currentTarget).siblings(".inline-editable").editable("show");
        this.siblingMatching(event.currentTarget, ".trigger-image-upload")?.click();
      }, 10);
    });

    this.scoped(".inline-editable").on("shown.templates", (event) => {
      const element = event.currentTarget;
      if (element.dataset.maxlength === undefined) { return; }

      element.parentElement.classList.add("editing");

      const container = this.siblingMatching(element, ".editable-container");
      const inputEl = container.querySelector(".editable-input input");
      inputEl.style.cssText = element.getAttribute("style") || "";
      inputEl.style.display = "inline-block";

      const maxlength = parseInt(element.dataset.maxlength, 10);
      $(inputEl).lilCharacterCount({
        limit: maxlength,
        warning: maxlength - 10,
        allowExceed: false,
        counterClass: "editable-counter",
        defaultClass: "bg-info",
        warningClass: "bg-warning",
        dangerClass: "bg-danger",
        submitButton: ""
      });

      const currentLength = maxlength - inputEl.value.length;
      const counterWrapper = document.createElement("div");
      counterWrapper.className = "editable-counter-wrapper";
      const totalCount = document.createElement("span");
      totalCount.className = "total-count";
      totalCount.textContent = `/${maxlength} characters`;
      counterWrapper.appendChild(totalCount);
      inputEl.insertAdjacentElement("afterend", counterWrapper);

      // S4 (jQuery `.html()` finding this replaced): `currentLength` is a controller-computed
      // integer, never template content, but this moves it into a real text node instead of an
      // HTML string regardless, so it can't become an XSS surface even if a later edit starts
      // interpolating something richer here.
      const counter = container.querySelector(".editable-counter");
      counter.textContent = currentLength;
      totalCount.before(counter);
    });

    this.scoped(".inline-editable").on("hidden.templates", (event) => {
      event.currentTarget.parentElement.classList.remove("editing");
    });

    // A global, not per-instance, template override; reapplying it on every connect is harmless.
    $.fn.editableform.buttons =
      `<button type="button" class="editable-cancel"><i class="icon-lasso-close"></i></button>
        <button type="submit" class="editable-submit"><p>Save</p></button>
        <div class="arrow"></div>`;

    const controller = this;

    // `this` inside bootstrap-editable's `success` callback is the field being edited (the
    // plugin's own convention, same as spectrum's) - kept as a regular function rather than an
    // arrow one because the native element itself, not an event target, is all the callback
    // receives.
    this.scoped(".inline-editable").editable({
      mode: "inline",
      display: false,
      success: function(response, newValue) {
        const node = this.id.split("-")[0];
        const oldValue = this.textContent;
        const type = this.dataset.textType || "text";

        this.textContent = newValue;

        controller.updateSettings(node, type, newValue, (error) => {
          if (error) { this.textContent = oldValue; }
        });
      }
    });

    this.scoped("#template_name").on("keyup.templates", () => this.validateCreate());
  }

  unbindTextEvents() {
    this.scoped(".edit-icon").off(".templates");
    this.scoped(".inline-editable").off(".templates").editable("destroy");
    this.scoped("#template_name").off(".templates");
  }

  // ---- color pickers (spectrum) ----

  bindColorPickers() {
    const controller = this;

    this.scoped(".template-color-picker").spectrum({
      type: "text",
      preferredFormat: "hex",
      showInput: true,
      showPalette: true,
      palette: [
        ["#333333", "#555555", "#777777", "#1377CD", "#585D85", "#213752", "#87A950", "#448475", "#004F61"],
        ["#EE9911", "#FFD061", "#FFC692", "#FF7755", "#CF1322", "#F6737E", "#855ECB", "#8285A3", "#BCEFFF"]
      ],
      showAlpha: false,
      allowEmpty: false,
      // `this` here is the specific `.template-color-picker` element spectrum was called on -
      // the plugin's own convention, kept (rather than an arrow function) because it reads a
      // data attribute off it.
      change: function(color) {
        const node = this.dataset.target;
        controller.updateSettings(node, "color", color.toHexString(), () => {});
      },
      move: function(color) {
        controller.updateColorInTemplate(this, color);
      },
      hide: function(color) {
        controller.updateColorInTemplate(this, color);
      },
      show: function() {
        const colorCode = this.querySelector(".color-code").textContent;
        $(this).spectrum("set", colorCode);
      }
    });
  }

  unbindColorPickers() {
    // Safe on an uninitialized element too - the plugin's own "destroy" dispatch no-ops when it
    // finds nothing under its internal `spectrum.id` data key.
    this.scoped(".template-color-picker").spectrum("destroy");
  }

  updateColorInTemplate(element, color) {
    element.querySelector(".color-code").textContent = color.toHexString().toUpperCase();
    element.querySelector(".color-sample").style.backgroundColor = color;

    const node = element.dataset.target;
    const isButtons = element.dataset.nodeType === "buttons";

    if (isButtons) {
      this.scoped(`#${node}-buttons`).children(".btn").each((index, button) => {
        if (button.classList.contains("btn-borderless")) {
          button.style.color = color;
        } else {
          button.style.backgroundColor = color;
          button.style.borderColor = color;
        }
      });
    } else {
      this.scoped(`#${node}-text`).css("color", color);
    }
  }

  // ---- visibility ----

  bindVisibilityCheckboxes() {
    this.scoped(".visibility-checkbox").on("click.templates", (event) => {
      const checkbox = event.currentTarget;
      const target = checkbox.parentElement.dataset.target;
      const node = target.split("-")[0];
      const $node = this.scoped(`#${target}`);

      setTimeout(() => {
        if (checkbox.checked) {
          $node.removeClass("translucent");
          this.updateVisibility(node, "true");
        } else {
          $node.addClass("translucent");
          this.updateVisibility(node, "false");
        }
      }, 0);
    });
  }

  unbindVisibilityCheckboxes() {
    this.scoped(".visibility-checkbox").off(".templates");
  }

  updateVisibility(node, visible) {
    this.updateSettings(node, "visible", visible, () => {});
  }

  validateCreate() {
    // Dead in every reachable view (no `#template_name`/`#continue-button` render anywhere -
    // superseded by `components/_new_template_modal.html.haml`'s own plain JS); kept as a no-op
    // only because `#template_name` keyup is still bound above for byte-parity with the source.
  }
}
